import { FC, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  AppBar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  TextFieldProps,
  Toolbar,
  Typography,
} from '@mui/material';
import CampaignCsvReader from '../CampaignCsvReader/CampaignCsvReader';
import { CampaignModal, CouponMultipleRecipients } from '@models';
import { LoadingButton } from '@mui/lab';
import { generateUniqueKey } from '@helpers';
import { useCreateMultipleCouponsMutation, useGetCampaignsQuery } from '@services';
import { useAppDispatch } from '@hooks/redux-custom';
import { showToast } from '@store/slices/notification';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import moment, { Moment } from 'moment';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import CampaignUploadProgress from './CampaignUploadProgress';
import CloseIcon from '@mui/icons-material/Close';
import UploadIcon from '@mui/icons-material/Upload';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const CampaignCsvUploadModal: FC<Props> = ({ isOpen, onClose }) => {
  const [invalidHeaders, setInvalidHeaders] = useState<string[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignModal | null>(null);
  const [couponNumberValue, setCouponNumberValue] = useState<number>(1);
  const [customerIdsArray, setCustomerIdsArray] = useState<string[]>([]);
  const [openConfirmPreviewModal, setOpenConfirmPreviewModal] = useState(false);
  const [expirationDate, setExpirationDate] = useState<Moment>(moment().add(1, 'days'));
  const [createCoupons, { isLoading: createCouponsIsLoading }] = useCreateMultipleCouponsMutation();

  const dispatch = useAppDispatch();

  const { data: campaigns } = useGetCampaignsQuery();

  const allCampaigns = campaigns?.data;

  const hasErrors = () => invalidHeaders.length > 0 || validationErrors.length > 0;

  const clearState = () => {
    setInvalidHeaders([]);
    setValidationErrors([]);
    setCustomerIdsArray([]);
  };

  const clearExitState = () => {
    setInvalidHeaders([]);
    setValidationErrors([]);
    setSelectedCampaign(null);
    setCouponNumberValue(1);
    setCustomerIdsArray([]);
    setExpirationDate(moment().add(1, 'days'));
  };

  useEffect(() => {
    !isOpen && clearExitState();
  }, [isOpen]);

  const addValidationError = (error: string) => {
    // Only track one instance of any given error to prevent the list from growing too large.
    if (validationErrors.includes(error)) return;

    const newArray = validationErrors;

    newArray.push(error);

    setValidationErrors(newArray);
  };

  const renderErrorAlert = (title: string, errorArray: string[]) => (
    <Alert severity="error" sx={{ my: 2 }}>
      <AlertTitle>{title}</AlertTitle>
      <ul>
        {errorArray.map((item) => (
          <li key={generateUniqueKey()}>{item}</li>
        ))}
      </ul>
    </Alert>
  );

  const renderErrors = () =>
    hasErrors() && (
      <>
        <Typography marginTop={2} variant="h6">
          The following errors must be resolved
        </Typography>
        {invalidHeaders.length > 0 && renderErrorAlert('The following headers are invalid', invalidHeaders)}
        {validationErrors.length > 0 && renderErrorAlert('The following values are invalid', validationErrors)}
      </>
    );

  const createCouponsForMultipleCustomers = (customerIds: string[], numberOfCoupons: number, totalCount: number) => {
    return new Promise((resolve, reject) => {
      if (totalCount < numberOfCoupons) {
        try {
          const payload: CouponMultipleRecipients = {
            campaignId: selectedCampaign?.id ?? 0,
            customerIds: customerIds,
            usageLimit: 1,
            numberOfCoupons: numberOfCoupons,
            startDate: new Date(),
            expiryDate: new Date(expirationDate.toDate()),
            attributes: {
              coupon_name: selectedCampaign?.name ?? '',
            },
          };

          createCoupons(payload);

          createCouponsForMultipleCustomers(customerIds, numberOfCoupons, totalCount + 1).then(() => {
            resolve(true);

            dispatch(
              showToast({
                message: 'Voucher attributes successfully applied.',
                type: 'success',
              }),
            );
          });
        } catch (error) {
          reject(error);

          dispatch(
            showToast({
              message: `Failed to create voucher attributes!`,
              type: 'error',
            }),
          );
        }
      } else {
        resolve(true);
      }
    });
  };

  // loop through all customers by Id and generate coupons
  const loopThroughCustomersAndCreateCoupons = (count: number, totalCustomerIds: number, totalIds: string[]) => {
    try {
      if (count < totalCustomerIds) {
        const customerIds = totalIds.slice(count, count + 1000);

        createCouponsForMultipleCustomers(customerIds, couponNumberValue, 0).then(() => {
          count = count + 1000;
          return loopThroughCustomersAndCreateCoupons(count, totalCustomerIds, totalIds);
        });
      } else {
        return true;
      }
    } catch (err) {
      console.log(err);
    }
  };

  // eslint-disable-next-line prefer-const
  let count = 0;

  const handleUpload = async () => {
    loopThroughCustomersAndCreateCoupons(count, customerIdsArray.length, customerIdsArray);
  };

  const ConfirmPreviewModal: FC = () => {
    const handleClose = () => {
      setOpenConfirmPreviewModal(false);
    };

    return (
      <Dialog open={openConfirmPreviewModal} fullScreen onClose={handleClose}>
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              Do you really want to apply the voucher to the eligible customers?
            </Typography>
            <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <DialogContent>
          {createCouponsIsLoading ? (
            <Grid item sm={12}>
              <CampaignUploadProgress count={count} totalCustomerCount={customerIdsArray.length} />
            </Grid>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Customer</TableCell>
                    <TableCell>Campaign</TableCell>
                    <TableCell># Coupons</TableCell>
                    <TableCell>Expiry Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customerIdsArray.length > 0 &&
                    customerIdsArray.map((cust: any) => (
                      <TableRow key={generateUniqueKey()}>
                        <TableCell component="th" scope="row">
                          {cust}
                        </TableCell>
                        <TableCell>{selectedCampaign?.name}</TableCell>
                        <TableCell>{couponNumberValue}</TableCell>
                        <TableCell>{expirationDate.format('DD/MM/YYYY')}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <LoadingButton
            sx={{ ml: 2 }}
            type="button"
            variant="contained"
            loading={createCouponsIsLoading}
            onClick={() => {
              handleUpload();

              if (!createCouponsIsLoading) {
                handleClose();
              }
            }}
          >
            Apply
          </LoadingButton>
        </DialogActions>
      </Dialog>
    );
  };

  const disableButton =
    !selectedCampaign ||
    !couponNumberValue ||
    couponNumberValue < 1 ||
    !expirationDate.isValid() ||
    customerIdsArray.length === 0 ||
    hasErrors();

  return (
    <Dialog open={isOpen} fullWidth onClose={onClose}>
      <DialogTitle>Upload CSV Campaign</DialogTitle>
      {onClose && (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      )}
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item sm={12}>
            <FormControl fullWidth>
              <InputLabel>Select Campaign *</InputLabel>
              <Select
                role="listbox"
                value={selectedCampaign?.id || ''}
                label="Select Campaign *"
                defaultValue={allCampaigns?.length ? allCampaigns[0].id : undefined}
                onChange={(evt) => {
                  const campaign = allCampaigns?.find((c) => c.id === evt.target.value);

                  setSelectedCampaign(campaign ? campaign : null);
                }}
              >
                {allCampaigns?.map((campaign) => (
                  <MenuItem key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item sm={12}>
            <TextField
              type="number"
              label="Number of Coupons *"
              value={couponNumberValue || ''}
              onChange={(evt) => {
                setCouponNumberValue(evt.target.value as unknown as number);
              }}
              fullWidth
            />
          </Grid>
          <Grid item sm={12}>
            <Box flex={1}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  value={expirationDate}
                  label="Expiry Date *"
                  minDate={moment().add(1, 'days')}
                  onChange={(newValue: Moment | null) => {
                    if (newValue) {
                      setExpirationDate(newValue);
                    }
                  }}
                  inputFormat="DD/MM/YYYY"
                  renderInput={(params: JSX.IntrinsicAttributes & TextFieldProps) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Box>
          </Grid>
          <Grid item sm={12}>
            <CampaignCsvReader
              addInvalidHeader={(header) => setInvalidHeaders([...invalidHeaders, header])}
              addValidationError={addValidationError}
              clearState={clearState}
              setCustomers={setCustomerIdsArray}
            />
            {renderErrors()}
          </Grid>
        </Grid>
        <ConfirmPreviewModal />
      </DialogContent>
      <DialogActions>
        <Button
          sx={{ ml: 2 }}
          type="button"
          variant="contained"
          disabled={disableButton}
          onClick={() => {
            setOpenConfirmPreviewModal(true);
          }}
          endIcon={<UploadIcon />}
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CampaignCsvUploadModal;
